/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
/* eslint-disable security/detect-non-literal-regexp */
import { PriceComponent } from '@msdyn365-commerce/components';
import { getRichTextHtml, IActionContext, IImageSettings, Image, ITelemetry } from '@msdyn365-commerce/core';
import { format, ProductPrice, ProductType, SearchSuggestion, SimpleProduct, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy';
import { getProductPageUrlSync, IProductInventoryInformation } from '@msdyn365-commerce-modules/retail-actions';
import {
    getPayloadObject,
    getTelemetryAttributes,
    INodeProps,
    Modal,
    ModalBody,
    IPayLoad,
    ITelemetryContent,
    NodeTag
} from '@msdyn365-commerce-modules/utilities';
import { ICartState } from '@msdyn365-commerce/global-state';
import * as React from 'react';
import classnames from 'classnames';

import { IDlvrySearchData } from '../dlvry-search.data';
import { IDlvrySearchProps, IDlvrySearchResources } from '../dlvry-search.props.autogenerated';
import BuySection from './buy-section';
import PQueue from 'p-queue';
import { BuyValidationResult } from '../../../themes/dlvry-adventureworks/views/components/product.component';
import { multiplyPriceBy } from '../../../shared-utils/price-utils';
import { humanizeUnitOfMeasure } from '../../../components/unit-of-measure';
import { ProductSearchResultExt, SimpleProductExt } from '../../../shared-utils/types';
import { formatCurrencyNOK, getProductPageUrlWithCategoryPathSync, isProductInCategory } from '../../../shared-utils/common-utils';
import { LifecycleStatus } from '../../../shared-utils/lifecycle-status';
import { Availability } from '../../../components/dropdown/availability';
import QuantityInput from '../../../components/common/quantity-input';
import { getKilogramConversion, getLiterPrice, getNonBreakoffUOM, getPcsConversion, getRoundedFactor } from '../../../shared-utils/unit-conversion-utils';
import { AddToCartComponent, IAddToCartToastResources } from '../../dlvry-buybox/common/add-to-cart.component';
import { getHideConversionInCategories } from '../../../settings/app.settings.utils';
import CartCount from '../../../components/cart-count';
import { IProductRelationTypeForProduct } from '../../../actions/get-relation-types-by-ids';
import { AlternativeProducts } from '../../../components/dropdown/alternative-products';
import { OnChangeProductFunction } from '../../../components/dropdown/alternative-products';
import * as GTM from '../../../shared-utils/google-tag-manager/gtm';
import { getShowAlternativeProductsType } from '../../../shared-utils/common-utils';
import { ProductListProduct } from '../../../components/product-list-component/product-list-component';
import { CartHelper } from '../../../shared-utils/cart-helper';
import { hasAgreementForProduct } from '../../../shared-utils/agreement-product-utils';
import { IProductAgreement, IProductExtended } from '../../../shared-utils/types';

/**
 * Search product suggest resources.
 */
export interface ISearchProductSuggestResources {
    freePriceText: string;
    inCartTooltipText: string;
    pricePerText: string;
}

export interface ISearchProductSuggestProps {
    searchProductSuggestClass: string;
    searchPageURL: string;
    searchQsp: string | undefined;
    hitPrefix: string;
    hitSuffix: string;
    autoSuggestProps: IDlvrySearchProps<IDlvrySearchData>;
    searchText: string;
    suggestions: SearchSuggestion[] | undefined;
    telemetry: ITelemetry;
    moduleType: string;
    moduleId: string;
    telemetryContent?: ITelemetryContent;
    locale: string;
    resources?: ISearchProductSuggestResources;
    activePrices: { [key: string]: ProductPrice };
    simpleProducts: SimpleProduct[];
    cart: ICartState | undefined;
    cartQueue: PQueue | undefined;
    unitConversions: UnitOfMeasureConversion[] | undefined;
    availabilities: IProductInventoryInformation[] | undefined;
    relationTypesByIds?: IProductRelationTypeForProduct[];
    customerAgreements?: IProductAgreement[];
    quantities: Map<number, number>;
    breakoffs: Map<number, boolean>;
    onQuantityChange: (productRecordId: number, quantity: number) => void;
    onBreakoffChange: (productRecordId: number, breakoff: boolean) => void;
    onBuyValidate: (product: ProductSearchResultExt, quantity: number) => Promise<BuyValidationResult>;
}

export interface ISearchProductViewProps {
    title: React.ReactNode;
    items: ISearchProductItemViewProps[];
}

export interface ISearchProductItemViewProps {
    text: React.ReactNode;
    thumbnail: React.ReactNode;
    price: React.ReactNode;
    totalPrice: React.ReactNode;
    buySection: React.ReactNode;
    availability?: React.ReactNode;
    quantity?: React.ReactNode;
    addToCart?: React.ReactNode;
    LiProduct: INodeProps;
    AProduct: INodeProps;
    id: string | undefined;
}

let payLoad: IPayLoad;
let telemetryContent: ITelemetryContent | undefined;

interface ISearchSuggestionProductState {
    availability: IProductInventoryInformation | undefined;
    allowBuying: boolean;
    isOutOfSeason: boolean;
    showAlternativeProductsType: 'Primary' | 'Secondary' | undefined;
    isProductPending: boolean;
    isAvailabilityPending: boolean;
}

interface IAlternativeProductsTriggerProps {
    alternativeButtonText: string;
    alternativeProductsChangeProductButtonText: string;
    cart: ICartState | undefined;
    cartQueue: PQueue | undefined;
    context: IDlvrySearchProps<IDlvrySearchData>['context'];
    dropdownProduct: ProductListProduct;
    id: string;
    onBuyValidate: (product: ProductSearchResultExt, quantity: number) => Promise<BuyValidationResult>;
    relationTypesByIds?: IProductRelationTypeForProduct[];
    showCompactAlternativeButton: boolean;
    typeName: string;
}

/**
 * ProductSuggestionsComponent.
 * @param props
 */
export const ProductSuggestionsComponent = (props: ISearchProductSuggestProps): ISearchProductViewProps | undefined => {
    const {
        suggestions,
        hitSuffix,
        hitPrefix,
        autoSuggestProps: {
            context: { actionContext },
            resources: { productSuggestionHeading }
        },
        moduleType,
        moduleId,
        quantities,
        breakoffs,
        onQuantityChange,
        onBreakoffChange
    } = props;
    const reactNodes: ISearchProductItemViewProps[] = [];
    telemetryContent = props.telemetryContent;
    payLoad = getPayloadObject('click', props.telemetryContent!, '');

    suggestions?.map(item => {
        const quantity = quantities.get(parseInt(item.Id!, 10)) || 0;
        const breakoff = breakoffs.get(parseInt(item.Id!, 10)) || false;
        reactNodes.push(_assembleNode(item, hitPrefix, hitSuffix, actionContext, props, moduleType, moduleId, quantity, breakoff, onQuantityChange, onBreakoffChange));
    });

    const titleNode = _renderTitle(productSuggestionHeading);

    return { title: titleNode, items: reactNodes };
};

const _renderTitle = (productSuggestionHeading: string) => {
    const productAutoSuggestionHeading = productSuggestionHeading ? productSuggestionHeading : 'Go to products';

    return <li className='msc-autoSuggest__productResults-title'>{productAutoSuggestionHeading}</li>;
};

const _assembleNode = (
    item: SearchSuggestion,
    hitPrefix: string,
    hitSuffix: string,
    actionContext: IActionContext,
    props: ISearchProductSuggestProps,
    moduleType: string,
    moduleId: string,
    quantity: number,
    breakoff: boolean,
    onQuantityChange: (productRecordId: number, quantity: number) => void,
    onBreakoffChange: (productRecordId: number, breakoff: boolean) => void
): ISearchProductItemViewProps => {
    const altText =
        item.Value && item.Value.replace(new RegExp(`[${hitPrefix}]`, 'gi'), '').replace(new RegExp(`[${hitSuffix}]`, 'gi'), '');

    const price = props.activePrices[item.Id!];
    const simpleProduct = props.simpleProducts.find(product => product.RecordId === parseInt(item.Id!, 10));
    const totalPrice = _renderTotalPrice(price, simpleProduct, quantity, breakoff, props);

    return {
        text: _getHighlightedText(item.Value ? item.Value : '', hitPrefix, hitSuffix, simpleProduct, props.customerAgreements),
        thumbnail: _renderThumbnail(item, altText ? altText : '', props),
        price: _renderPrice(price, simpleProduct, moduleId, moduleType, props, quantity, breakoff),
        totalPrice: totalPrice,
        buySection: _renderBuySection(item.Id!, props, quantity, breakoff, onQuantityChange, onBreakoffChange, totalPrice),
        availability: _renderAvailability(item.Id, props),
        quantity: _renderQuantity(item.Id!, props, quantity, breakoff, onQuantityChange, onBreakoffChange),
        addToCart: _renderAddToCart(item.Id, simpleProduct, props, quantity, breakoff, onQuantityChange),
        LiProduct: _getLiWrapper(),
        AProduct: _getAWrapper(item, simpleProduct, hitPrefix, hitSuffix, actionContext, props),
        id: item.Id
    };
};

const _getLiWrapper = () => {
    return {
        className: 'msc-autoSuggest__productResults-item',
        tag: 'li' as NodeTag
    };
};

const _getAWrapper = (
    item: SearchSuggestion,
    simpleProduct: SimpleProductExt | undefined,
    hitPrefix: string,
    hitSuffix: string,
    actionContext: IActionContext,
    props: ISearchProductSuggestProps
) => {
    const ariaLabel =
        item.Value && item.Value.replace(new RegExp(`[${hitPrefix}]`, 'gi'), '').replace(new RegExp(`[${hitSuffix}]`, 'gi'), '');

    payLoad.contentAction.recid = item.Id;
    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);
    const productPrice = item.Value3?.Value?.DecimalValue ? formatCurrencyNOK(item.Value3.Value.DecimalValue) : undefined;

    return {
        className: 'msc-autoSuggest__productResults-item',
        tag: 'a' as NodeTag,
        'aria-label': `${ariaLabel!} ${productPrice || ''}`,
        href: _generateLink(item, simpleProduct, hitPrefix, hitSuffix, actionContext),
        ...attribute
    };
};

const _generateLink = (suggestion: SearchSuggestion, simpleProduct: SimpleProductExt | undefined, hitPrefix: string, hitSuffix: string, actionContext: IActionContext) => {
    const formattedLink = (suggestion.Value as string)
        .replace(new RegExp(`[${hitPrefix}]`, 'gi'), '')
        .replace(new RegExp(`[${hitSuffix}]`, 'gi'), '');
    const productUrl = simpleProduct ? getProductPageUrlWithCategoryPathSync(actionContext, simpleProduct, simpleProduct.CategoryPaths) : undefined;
    return suggestion.Value && suggestion.Id
        ? productUrl || getProductPageUrlSync(formattedLink, (suggestion.Id as unknown) as number, actionContext, undefined)
        : '';
};

const _getHighlightedText = (
    suggestion: string,
    hitPrefix: string,
    hitSuffix: string,
    simpleProduct: SimpleProduct | undefined,
    customerAgreements: IProductAgreement[] | undefined
): JSX.Element => {
    const formattedText = suggestion
        .replace(new RegExp(`[${hitPrefix}]`, 'gi'), '<strong>')
        .replace(new RegExp(`[${hitSuffix}]`, 'gi'), '</strong>');
    const showAgreementBadge = simpleProduct
        ? hasAgreementForProduct(simpleProduct as SimpleProduct & IProductExtended, customerAgreements)
        : false;

    return (
        <div className='msc-autoSuggest__productResults-item-text-wrapper'>
            {showAgreementBadge && (
                <span className='msc-autoSuggest__productResults-agreement-product' title='Avtalevare'>A</span>
            )}
            {/* eslint-disable-next-line react/no-danger */}
            <span className='msc-price msc-autoSuggest__productResults-item-text' dangerouslySetInnerHTML={getRichTextHtml(formattedText)} />
        </div>
    );
};

const _getSearchSuggestionProductState = (
    simpleProduct: SimpleProduct | undefined,
    props: ISearchProductSuggestProps
): ISearchSuggestionProductState => {
    const isProductPending = simpleProduct === undefined;
    const availability = props.availabilities?.find(x => x.ProductAvailableQuantity.ProductId === simpleProduct?.RecordId);
    const lifeCycleStatus = simpleProduct?.ExtensionProperties?.find(x => x.Key === 'PRODUCTLIFECYCLESTATEID')?.Value?.StringValue;
    const isOutOfSeason = lifeCycleStatus === LifecycleStatus.OutOfSeason;
    const relationTypesForProduct = props.relationTypesByIds?.filter(x => x.ProductId === simpleProduct?.RecordId) || [];
    const showAlternativeProductsType = getShowAlternativeProductsType(props.autoSuggestProps.context, relationTypesForProduct, availability);
    const isAvailabilityPending = !!simpleProduct && (isOutOfSeason || showAlternativeProductsType === 'Primary') && availability === undefined;
    const allowBuying = props.autoSuggestProps.context.request.user.isAuthenticated &&
        !isProductPending &&
        ((isOutOfSeason || showAlternativeProductsType === 'Primary') ? !isAvailabilityPending && availability?.StockLevelCode !== 'OOS' : true);

    return {
        availability,
        allowBuying,
        isOutOfSeason,
        showAlternativeProductsType,
        isProductPending,
        isAvailabilityPending
    };
};

const AlternativeProductsTrigger = (props: IAlternativeProductsTriggerProps) => {
    const {
        alternativeButtonText,
        alternativeProductsChangeProductButtonText,
        cart,
        cartQueue,
        context,
        dropdownProduct,
        id,
        onBuyValidate,
        relationTypesByIds,
        showCompactAlternativeButton,
        typeName
    } = props;
    const [isOpen, setIsOpen] = React.useState(false);
    const onChangeProduct: OnChangeProductFunction = async (
        _product,
        replacement,
        replacementSimpleProduct,
        unitConversions,
        _recommendationType,
        _position,
        quantity,
        breakoff
    ): Promise<boolean> => {
        if (!cart || !cartQueue || !replacementSimpleProduct) {
            return false;
        }

        const replacementProductSearch: ProductSearchResultExt = {
            RecordId: replacement.RecordId,
            Brand: replacement.Brand,
            Category: replacement.Category,
            CategoryPaths: replacement.CategoryPaths,
            ItemId: replacement.ItemId,
            Name: replacement.Name,
            Price: replacement.ProductPrice?.CustomerContextualPrice || replacement.Price || 0
        };

        const queuedResult = await cartQueue.add<boolean>(async (): Promise<boolean> => {
            return CartHelper.addToCartAsync(
                context,
                replacement,
                replacementSimpleProduct,
                true,
                quantity,
                breakoff,
                () => onBuyValidate(replacementProductSearch, quantity),
                getNonBreakoffUOM(replacementSimpleProduct, unitConversions)
            );
        });

        return queuedResult === true;
    };

    return (
        <>
            <button
                className={classnames('ms-search__change-product-button', {
                    compact: showCompactAlternativeButton,
                    standalone: !showCompactAlternativeButton
                })}
                onClick={() => {
                    void GTM.beginAltProductReplaceedAsync(dropdownProduct);
                    setIsOpen(true);
                }}
                title={alternativeButtonText}
                type='button'
            >
                {!showCompactAlternativeButton && alternativeButtonText}
            </button>
            <Modal className='msc-modal-input-required ms-search__alternative-products-modal' isOpen={isOpen} toggle={() => setIsOpen(false)}>
                <ModalBody>
                    <AlternativeProducts
                        id={id}
                        typeName={typeName}
                        context={context}
                        mode='edit'
                        resources={{
                            alternativeProductsChangeProductButtonText,
                            alternativeProductsLoadingText: 'Laster alternativer...',
                            alternativeProductsNoResultsText: 'Ingen alternativer funnet',
                            alternativeProductsErrorText: 'Kunne ikke laste alternativer',
                            alternativeProductsCloseButtonText: 'Lukk'
                        }}
                        product={dropdownProduct}
                        relationTypesByIds={relationTypesByIds}
                        onChangeProduct={onChangeProduct}
                        close={() => setIsOpen(false)}
                    />
                </ModalBody>
            </Modal>
        </>
    );
};

const _renderPrice = (
    price: ProductPrice | undefined,
    simpleProduct: SimpleProduct | undefined,
    moduleId: string,
    moduleType: string,
    props: ISearchProductSuggestProps,
    quantity: number,
    breakoff: boolean
): JSX.Element | null => {
    const {
        autoSuggestProps: { context, resources }
    } = props;

    if (!context.request.user.isAuthenticated) {
        return (
            <div className='msc-autoSuggest__productResults-prices not-logged-in'>
                <span className='msc-price msc-autoSuggest__productResults-item-price' style={{ marginTop: 2 }}>
                    {resources.priceNotLoggedInText}
                </span>
            </div>
        );
    }

    const { allowBuying, isOutOfSeason, isProductPending, isAvailabilityPending } = _getSearchSuggestionProductState(simpleProduct, props);

    if (isProductPending || isAvailabilityPending) {
        return (
            <div className='msc-autoSuggest__productResults-prices'>
                <span className='msc-price msc-autoSuggest__productResults-item-price'>
                    <span className='msc-price__actual retrieving'>
                        ...
                    </span>
                </span>
            </div>
        );
    }

    if (!allowBuying && isOutOfSeason) {
        return (
            <div className='msc-autoSuggest__productResults-prices'>
                <span className='msc-autoSuggest__productResults-out-of-season'>
                    Varen er ute av sesong
                </span>
            </div>
        );
    }

    if (!allowBuying) {
        return <div className='msc-autoSuggest__productResults-prices' />;
    }

    if (!price || !simpleProduct) {
        return (
            <div className='msc-autoSuggest__productResults-prices'>
                <span className='msc-price msc-autoSuggest__productResults-item-price'>
                    <span className='msc-price__actual retrieving'>
                        ...
                    </span>
                </span>
            </div>
        );
    }

    let productPrice = price;

    if (!breakoff && simpleProduct.Behavior?.MultipleOfQuantity) {
        productPrice = multiplyPriceBy(productPrice, simpleProduct.Behavior.MultipleOfQuantity);
    }

    // Check if we should kg price for this product based on category settings
    const hideConversionInCategories = getHideConversionInCategories(context);
    const dontShowKgPrice = isProductInCategory(simpleProduct, hideConversionInCategories);
    const conversions = props.unitConversions?.filter(x => x.ProductRecordId === simpleProduct.RecordId);
    const kilogramConversion = !dontShowKgPrice && conversions && getKilogramConversion(simpleProduct.DefaultUnitOfMeasure, conversions);
    const pcsConversion = conversions && getPcsConversion(conversions, simpleProduct.DefaultUnitOfMeasure);
    const literPrice = conversions && getLiterPrice(conversions, simpleProduct.DefaultUnitOfMeasure, price);

    return (
        <div className='msc-autoSuggest__productResults-prices'>
            <PriceComponent
                id={moduleId}
                typeName={moduleType}
                context={context}
                data={{
                    price: {
                        CustomerContextualPrice: productPrice.CustomerContextualPrice
                    }
                }}
                className='msc-autoSuggest__productResults-item-price'
                freePriceText={props.resources?.freePriceText}
            />
            {!breakoff && (!!simpleProduct.Behavior?.MultipleOfQuantity && simpleProduct.Behavior.MultipleOfQuantity > 1) && (
                <div className='msc-autoSuggest__productResults-item-price-per__wrapper'>
                    <PriceComponent
                        id={moduleId}
                        typeName={moduleType}
                        context={context}
                        data={{
                            price: {
                                CustomerContextualPrice: price.CustomerContextualPrice
                            }
                        }}
                        className='msc-autoSuggest__productResults-item-price-per'
                        freePriceText={props.resources?.freePriceText}
                    />
                    <span>
                        &nbsp;
                        {format(props.resources?.pricePerText || 'pr. {0}', humanizeUnitOfMeasure(props.locale, simpleProduct.DefaultUnitOfMeasure, simpleProduct).toLowerCase())}
                    </span>
                </div>
            )}
            {pcsConversion && (
                <div className='msc-autoSuggest__productResults-item-price-extra__wrapper'>
                    <PriceComponent
                        context={context}
                        data={{ price: { CustomerContextualPrice: (price.CustomerContextualPrice || 0) / (pcsConversion.Factor || 1) } }}
                        id={moduleId}
                        typeName={moduleType} />
                    <span>
                        &nbsp;
                        pr. stk
                    </span>
                </div>
            )}
            {kilogramConversion && getRoundedFactor(kilogramConversion) > 0 && (
                <div className='msc-autoSuggest__productResults-item-price-extra__wrapper'>
                    <PriceComponent
                        context={context}
                        data={{ price: { CustomerContextualPrice: (price.CustomerContextualPrice || 0) / getRoundedFactor(kilogramConversion) } }}
                        id={moduleId}
                        typeName={moduleType} />
                    <span>
                        &nbsp;
                        pr. kg
                    </span>
                </div>
            )}
            {literPrice && (
                <div className='msc-autoSuggest__productResults-item-price-extra__wrapper'>
                    <PriceComponent
                        context={context}
                        data={{ price: { CustomerContextualPrice: literPrice } }}
                        id={moduleId}
                        typeName={moduleType} />
                    <span>
                        &nbsp;
                        pr. liter
                    </span>
                </div>
            )}
            {/* <div className={classnames('msc-autoSuggest__productResults-total-price-wrapper', { retrieving: price === undefined })}>
                <div className='msc-autoSuggest__productResults-total-price-text'>
                    Totalt
                </div>
                <PriceComponent
                    context={context}
                    id={moduleId}
                    typeName={moduleType}
                    className='msc-autoSuggest__productResults-total-price-price'
                    data={{ price: { CustomerContextualPrice: (price.CustomerContextualPrice || 0) * quantity } }}
                    freePriceText=' '
                />
            </div> */}
        </div>
    );
};

const _renderTotalPrice = (
    price: ProductPrice | undefined,
    simpleProduct: SimpleProduct | undefined,
    quantity: number,
    breakoff: boolean,
    props: ISearchProductSuggestProps
): JSX.Element | null => {
    const { context, id, typeName } = props.autoSuggestProps;

    if (!context.request.user.isAuthenticated) {
        return null;
    }

    const { allowBuying } = _getSearchSuggestionProductState(simpleProduct, props);

    if (!allowBuying) {
        return <div className='msc-autoSuggest__productResults-total-price-slot' />;
    }

    const multiplier = breakoff ? 1 : simpleProduct?.Behavior?.MultipleOfQuantity || 1;
    const quantityIsNotDefault = quantity !== 0 && quantity !== multiplier;

    return (
        <div className={classnames('msc-autoSuggest__productResults-total-price-wrapper', { changed: quantityIsNotDefault, retrieving: price === undefined })}>
            <div className='msc-autoSuggest__productResults-total-price-text'>
                Totalt:
            </div>
            <PriceComponent
                context={context}
                id={id}
                typeName={typeName}
                className='msc-autoSuggest__productResults-total-price-price'
                data={{ price: { CustomerContextualPrice: (price?.CustomerContextualPrice || 0) * quantity } }}
                freePriceText=' '
            />
        </div>
    );
};

const _renderBuySection = (
    itemId: string,
    props: ISearchProductSuggestProps,
    quantity: number,
    breakoff: boolean,
    onQuantityChange: (productRecordId: number, quantity: number) => void,
    onBreakoffChange: (productRecordId: number, breakoff: boolean) => void,
    totalPrice?: React.ReactNode
): JSX.Element | null => {
    const productId = parseInt(itemId, 10);

    if (isNaN(productId) || !props.cart) {
        return null;
    }

    if (!props.autoSuggestProps.context.request.user.isAuthenticated) {
        return null;
    }

    const {
        id,
        typeName,
        context
    } = props.autoSuggestProps;
    const simpleProduct = props.simpleProducts.find(product => product.RecordId === productId);
    const productPrice = props.activePrices[itemId];

    return (
        <BuySection
            id={id}
            typeName={typeName}
            context={context}
            resources={props.resources as IDlvrySearchResources}
            productRecordId={productId}
            simpleProduct={simpleProduct}
            productPrice={productPrice}
            cart={props.cart}
            cartQueue={props.cartQueue}
            onBuyValidate={props.onBuyValidate}
            inCartTooltipText={props.resources?.inCartTooltipText}
            unitConversions={props.unitConversions?.filter(x => x.ProductRecordId === productId) || []}
            availability={props.availabilities?.find(x => x.ProductAvailableQuantity.ProductId === productId)}
            quantity={quantity}
            breakoff={breakoff}
            onQuantityChange={onQuantityChange}
            onBreakoffChange={onBreakoffChange}
            totalPrice={totalPrice}
        />
    );
};

const _renderAvailability = (id: string | undefined, props: ISearchProductSuggestProps): React.ReactNode => {
    if (!id) {
        return null;
    }

    const productId = parseInt(id, 10);

    if (isNaN(productId)) {
        return null;
    }

    const availability = props.availabilities?.find(x => x.ProductAvailableQuantity.ProductId === productId);

    return (
        <Availability
            context={props.autoSuggestProps.context}
            baseClassName='ms-search'
            availability={availability}
        />
    );
};

const _renderQuantity = (
    itemId: string,
    props: ISearchProductSuggestProps,
    quantity: number,
    breakoff: boolean,
    onQuantityChange: (productRecordId: number, quantity: number) => void,
    onBreakoffChange: (productRecordId: number, breakoff: boolean) => void
) => {
    const productId = parseInt(itemId, 10);

    if (isNaN(productId)) {
        return null;
    }

    const simpleProduct = props.simpleProducts.find(product => product.RecordId === productId);
    const { allowBuying, isProductPending, isAvailabilityPending } = _getSearchSuggestionProductState(simpleProduct, props);

    if (isProductPending || isAvailabilityPending) {
        return (
            <div className='msc-autoSuggest__productResults-quantity-slot'>
                <QuantityInput
                    context={props.autoSuggestProps.context}
                    product={undefined}
                    extProduct={undefined}
                    quantity={quantity}
                    breakoff={breakoff}
                    onQuantityChange={() => undefined}
                    onBreakoffChange={() => undefined}
                    nonBreakoffUOM={undefined}
                    unitConversions={props.unitConversions}
                />
            </div>
        );
    }

    if (!allowBuying) {
        return <div className='msc-autoSuggest__productResults-quantity-slot' />;
    }

    return (
        <QuantityInput
            context={props.autoSuggestProps.context}
            product={simpleProduct}
            extProduct={simpleProduct}
            quantity={quantity}
            breakoff={breakoff}
            onQuantityChange={quantity => onQuantityChange(productId, quantity)}
            onBreakoffChange={breakoff => onBreakoffChange(productId, breakoff)}
            nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, props.unitConversions)}
            unitConversions={props.unitConversions}
        />
    );
};

const _renderAddToCart = (
    productId: string | undefined,
    simpleProduct: SimpleProductExt | undefined,
    props: ISearchProductSuggestProps,
    quantity: number,
    breakoff: boolean,
    onQuantityChange: (productRecordId: number, quantity: number) => void
) => {
    const { cart, cartQueue } = props;
    const resources = props.resources as IDlvrySearchResources;
    const { context, id, typeName } = props.autoSuggestProps;
    const productRecordId = parseInt(productId!, 10);

    if (isNaN(productRecordId) || !cart) {
        return null;
    }

    const {
        availability,
        allowBuying,
        showAlternativeProductsType,
        isProductPending,
        isAvailabilityPending
    } = _getSearchSuggestionProductState(simpleProduct, props);
    const productPrice = props.activePrices[productId!];
    const toastStrings: IAddToCartToastResources = {
        errorHeader: resources.toastErrorHeader,
        productsAddedToCart: resources.productsAddedToCart,
        productsRemovedFromCart: resources.productsRemovedFromCart,
        productsUpdatedInCart: resources.productsUpdatedInCart,
        goToCart: resources.toastGoToCart,
        noAccessToProduct: resources.toastNoAccessToProduct
    };
    const productSearchResult: ProductSearchResultExt = {
        RecordId: productRecordId,
        Brand: simpleProduct?.Brand,
        Category: simpleProduct?.Category,
        CategoryPaths: simpleProduct?.CategoryPaths,
        ItemId: simpleProduct?.ItemId,
        Name: simpleProduct?.Name,
        Price: productPrice?.CustomerContextualPrice || 0
    };

    const placeholderProduct: SimpleProduct = {
        AdjustedPrice: 0,
        BasePrice: 0,
        Price: 0,
        ProductTypeValue: ProductType.None,
        RecordId: productRecordId
    };
    const dropdownProduct: ProductListProduct | undefined = simpleProduct ? {
        ...simpleProduct,
        RecordId: simpleProduct.RecordId,
        ItemId: simpleProduct.ItemId || '',
        Name: simpleProduct.Name || '',
        PrimaryImageUrl: simpleProduct.PrimaryImageUrl,
        DefaultUnitOfMeasure: simpleProduct.DefaultUnitOfMeasure || '',
        Price: productPrice?.CustomerContextualPrice || simpleProduct.Price || 0,
        BreakoffProductKey: simpleProduct.RecordId?.toString() || productRecordId.toString(),
        Availability: availability
    } : undefined;
    const showCompactAlternativeButton = allowBuying;
    const alternativeButtonText = 'Alternativ';

    return (
        <div className='msc-autoSuggest__productResults-add-to-cart-wrapper'>
            <CartCount
                context={context}
                cart={cart.cart}
                product={simpleProduct}
                nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, props.unitConversions)}
            />
            {showAlternativeProductsType && dropdownProduct && (
                <AlternativeProductsTrigger
                    alternativeButtonText={alternativeButtonText}
                    alternativeProductsChangeProductButtonText={props.autoSuggestProps.resources.alternativeProductsBuyButtonText}
                    cart={cart}
                    cartQueue={cartQueue}
                    context={context}
                    dropdownProduct={dropdownProduct}
                    id={id}
                    onBuyValidate={props.onBuyValidate}
                    relationTypesByIds={props.relationTypesByIds}
                    showCompactAlternativeButton={showCompactAlternativeButton}
                    typeName={typeName}
                />
            )}
            {(isProductPending || isAvailabilityPending) && !showAlternativeProductsType && (
                <div className='msc-autoSuggest__productResults-action-placeholder' />
            )}
            {!isProductPending && !isAvailabilityPending && !allowBuying && !showAlternativeProductsType && (
                <div className='msc-autoSuggest__productResults-action-slot' />
            )}
            {allowBuying && (
                <AddToCartComponent
                    id={id}
                    context={context}
                    typeName={typeName}
                    disabled={!simpleProduct || !productPrice}
                    quantity={quantity}
                    breakoff={breakoff}
                    productAvailability={availability?.ProductAvailableQuantity}
                    nonBreakoffUOM={getNonBreakoffUOM(simpleProduct, props.unitConversions)}
                    onUpdateQuantity={quantity => onQuantityChange(productRecordId, quantity)}
                    data={{
                        cart,
                        product: simpleProduct || placeholderProduct,
                        price: productPrice,
                        productSearch: productSearchResult
                    }}
                    cartQueue={cartQueue}
                    addToCartText={resources.addToCartButtonText}
                    removeFromCartText='WIP Fjern fra kurv'
                    updateCartText='WIP Oppdater'
                    expiredText={resources.expiredText}
                    outOfSeasonBuyButtonText={resources.outOfSeasonBuyButtonText}
                    toastStrings={toastStrings}
                    onBuyValidate={props.onBuyValidate}
                />
            )}
        </div>
    );
};

const _renderThumbnail = (suggestion: SearchSuggestion, altText: string, props: ISearchProductSuggestProps): JSX.Element | null => {
    const {
        autoSuggestProps: {
            context: {
                request: { gridSettings }
            },
            config: { imageSettings }
        }
    } = props;
    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }

    const defaultImageSettings: IImageSettings = {
        viewports: {
            lg: { q: 'w=64&h=64&m=0&f=webp', w: 64, h: 64 },
            md: { q: 'w=64&h=64&m=0&f=webp', w: 64, h: 64 },
            sm: { q: 'w=64&h=64&m=0&f=webp', w: 64, h: 64 },
            xl: { q: 'w=64&h=64&m=0&f=webp', w: 64, h: 64 },
            xs: { q: 'w=64&h=64&m=0&f=webp', w: 64, h: 64 }
        },
        lazyload: true,
        cropFocalRegion: true
    };

    if (imageSettings) {
        imageSettings.viewports = defaultImageSettings.viewports;
    }

    return (
        <Image
            requestContext={props.autoSuggestProps.context.actionContext.requestContext}
            altText={altText}
            className='msc-autoSuggest__productResults-item-img'
            src={suggestion.ImageUrl}
            gridSettings={gridSettings!}
            imageSettings={imageSettings ? imageSettings : defaultImageSettings}
            loadFailureBehavior='empty'
        />
    );
};
